// A clock for live countdowns, and what happens when one reaches zero.
// One timer for the whole page, running only while something shows a countdown, and set to the server's time
// (the Date header of the last data load) so the countdown ends when sales actually open, not when this computer thinks so.
import { readable } from 'svelte/store';
import { loadStoreData, serverOffset } from './storeData.js';

export const now = readable(Date.now() + serverOffset, (set) => {
  set(Date.now() + serverOffset);
  const t = setInterval(() => set(Date.now() + serverOffset), 1000);
  return () => clearInterval(t);
});

/** { days, hours, minutes, seconds } left until `iso`, never negative. */
export function remaining(iso, at) {
  const ms = Math.max(0, Date.parse(iso) - at);
  const s = Math.floor(ms / 1000);
  return { ms, days: Math.floor(s / 86400), hours: Math.floor(s / 3600) % 24, minutes: Math.floor(s / 60) % 60, seconds: s % 60 };
}

// ── Going on sale ──────────────────────────────────────────────────────────────
// When a countdown ends, the store data is loaded again so the product arrives with its stock and can be added to the cart.
// If the server has not opened it yet (clocks a second apart), it is asked again a few times.
let refreshing = null;
export function launchReached() {
  if (refreshing) return refreshing;
  refreshing = (async () => {
    for (let attempt = 0; attempt < 8; attempt++) {
      await new Promise(r => setTimeout(r, attempt === 0 ? 800 + Math.random() * 1200 : 3000));   // a little spread, so a crowd does not arrive at once
      try {
        const d = await loadStoreData(true);
        const at = Date.now() + serverOffset;
        if (!d.products.some(p => p.comingSoon && p.launchAt && Date.parse(p.launchAt) <= at)) break;
      } catch { /* try again */ }
    }
    refreshing = null;
  })();
  return refreshing;
}

// While a coming-soon product is on screen, check now and then for changes made in the admin (an earlier launch, a new date),
// and whenever the visitor comes back to the tab.
let watchers = 0;
let timer = null;
const quietRefresh = () => { if (document.visibilityState === 'visible') loadStoreData(true).catch(() => {}); };
export function watchLaunches() {
  if (typeof window === 'undefined') return () => {};
  if (watchers++ === 0) {
    timer = setInterval(quietRefresh, 2 * 60 * 1000);
    document.addEventListener('visibilitychange', quietRefresh);
  }
  return () => {
    if (--watchers === 0) { clearInterval(timer); document.removeEventListener('visibilitychange', quietRefresh); }
  };
}
